import React, { useState } from "react";
import { ArrowRight, Check, ChevronDown, HelpCircle, Info, Lock } from "lucide-react";
import { groupByCategory } from "../../lib/packageLogic";

const Card = ({ service, on, locked, onToggle, note, infoOpen, onInfo, badge }) => {
  const detail = note || service.description;
  return (
    <div
      className={`relative overflow-hidden rounded-xl border duration-200 [transition-property:border-color,background-color,box-shadow] ${
        on ? "border-orange bg-orange/[0.07] shadow-sm" : locked ? "border-slate-200 bg-slate-50/70" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`pricing-service-card-${service.service_id}`}
    >
      <div className="flex items-start gap-2 p-3.5">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={on}
          data-testid={`pricing-service-toggle-${service.service_id}`}
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
        >
          <span
            className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 duration-200 [transition-property:background-color,border-color] ${
              on ? "border-orange bg-orange text-white" : "border-slate-300 bg-white text-transparent"
            }`}
          >
            <Check size={13} strokeWidth={3} />
          </span>
          <span className="min-w-0">
            <span className="block font-display text-sm font-bold leading-snug text-navy">{service.name}</span>
            {badge && (
              <span
                data-testid={`pricing-service-badge-${service.service_id}`}
                className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                  badge === "required" ? "bg-slate-200 text-slate-600" : "bg-orange/15 text-orange-dark"
                }`}
              >
                {badge === "required" ? "Required with your selection" : "Recommended for you"}
              </span>
            )}
            {locked && (
              <span
                className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500"
                data-testid={`pricing-service-locked-${service.service_id}`}
              >
                <Lock size={10} /> Comes with Monthly Bookkeeping
              </span>
            )}
          </span>
        </button>
        {detail && (
          <button
            type="button"
            onClick={onInfo}
            aria-expanded={infoOpen}
            aria-label={`What ${service.name} covers`}
            data-testid={`pricing-service-info-${service.service_id}`}
            className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border duration-200 [transition-property:background-color,border-color,color] ${
              infoOpen ? "border-orange bg-orange text-white" : "border-slate-200 bg-white text-slate-500 hover:border-orange hover:text-orange"
            }`}
          >
            <HelpCircle size={15} />
          </button>
        )}
      </div>
      {infoOpen && detail && (
        <div className="border-t border-slate-100 px-3.5 py-3" data-testid={`pricing-service-desc-${service.service_id}`}>
          <p className="text-xs leading-relaxed text-slate-600">{detail}</p>
        </div>
      )}
    </div>
  );
};

// Stage 1. Categories come from the pricing platform, so a new service appears
// under the right heading by itself. Laid out in two flowing columns because most
// categories hold a single service — a grid left six of nine rows nearly empty.
export default function ServiceCards({
  services,
  selected,
  onToggle,
  lockedIds = [],
  standardItems = [],
  bookkeepingName = "Monthly Bookkeeping",
  promptFor = "",
  onAddBookkeeping,
  notes = {},
  recommendedIds = [],
  requiredIds = [],
}) {
  const [standardOpen, setStandardOpen] = useState(false);
  // One description open at a time, so the list stays short however much they read.
  const [info, setInfo] = useState("");
  const categories = groupByCategory((services || []).filter((s) => (s.display?.mode || "optional") !== "always"));

  return (
    <div data-testid="pricing-service-cards">
      <p className="mb-4 flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
        <HelpCircle size={12} /> Tap the question mark on any service to see what it covers
      </p>
      <div className="md:columns-2 md:gap-5 [column-fill:balance]">
        {categories.map((cat) => (
          <section
            key={cat.name}
            data-testid={`pricing-category-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
            className="mb-5 break-inside-avoid"
          >
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{cat.name}</h3>
            <div className="mt-2.5 space-y-2.5">
              {cat.services.map((s) => (
                <div key={s.service_id}>
                  <Card
                    service={s}
                    note={notes[s.service_id]}
                    on={selected.includes(s.service_id)}
                    locked={lockedIds.includes(s.service_id)}
                    onToggle={() => onToggle(s.service_id)}
                    infoOpen={info === s.service_id}
                    onInfo={() => setInfo((cur) => (cur === s.service_id ? "" : s.service_id))}
                    badge={
                      requiredIds.includes(s.service_id)
                        ? "required"
                        : recommendedIds.includes(s.service_id)
                          ? "recommended"
                          : ""
                    }
                  />
                  {promptFor === s.service_id && (
                    <div
                      className="mt-2 rounded-xl border border-orange/40 bg-orange/5 p-4"
                      data-testid={`pricing-service-prompt-${s.service_id}`}
                    >
                      <p className="flex items-start gap-2 text-xs leading-relaxed text-navy">
                        <Info size={13} className="mt-0.5 shrink-0 text-orange" />
                        <span>
                          <strong>{s.name}</strong> is part of our outsourced finance support, so it comes with {bookkeepingName}.
                        </span>
                      </p>
                      <button
                        type="button"
                        onClick={() => onAddBookkeeping(s.service_id)}
                        data-testid={`pricing-service-add-bookkeeping-${s.service_id}`}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-orange px-4 py-2.5 text-xs font-bold text-white duration-200 [transition-property:background-color] hover:bg-orange-dark"
                      >
                        Add both <ArrowRight size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Not a choice, so it sits after the choices rather than above them. */}
      {standardItems.length > 0 && (
        <div className="mt-2 rounded-xl border border-teal/30 bg-teal/[0.06] p-4" data-testid="pricing-standard-items">
          <button
            type="button"
            onClick={() => setStandardOpen((o) => !o)}
            aria-expanded={standardOpen}
            data-testid="pricing-standard-toggle"
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <span className="text-xs font-semibold text-navy">
              <span className="font-bold text-teal">Included as standard:</span>{" "}
              {standardItems.map((s) => s.name).join(", ")}
            </span>
            <ChevronDown size={15} className={`shrink-0 text-teal duration-200 [transition-property:transform] ${standardOpen ? "rotate-180" : ""}`} />
          </button>
          {standardOpen && (
            <div className="mt-3 space-y-3 border-t border-teal/20 pt-3" data-testid="pricing-standard-detail">
              {standardItems.map((s) => (
                <div key={s.service_id}>
                  <p className="text-xs font-bold text-navy">{s.name}</p>
                  {(s.display?.note || s.description) && (
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">{s.display?.note || s.description}</p>
                  )}
                </div>
              ))}
              <p className="text-[11px] text-slate-500">These apply to every new client, so they're part of your price rather than something to choose.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
